import { useInfiniteQuery } from '@tanstack/react-query';
import { useService } from '../providers/ServiceProvider.js';

export const FEED_TYPES = ['anti-debug', 'custom-rule', 'dependency-change', 'obfuscated-code'] as const;
export type AlertType = (typeof FEED_TYPES)[number];

export interface AlertFeedItem {
  id: string;
  createdDate: string;
  type: AlertType;
  severity: 'high' | 'medium' | 'low' | 'info';
  name: string | null;
  message: string | null;
  requestUrl: string | null;
  analysisId: string;
  projectId: string;
  projectName: string;
}

export interface AlertFeedPage {
  items: AlertFeedItem[];
  nextCursor: string | null;
}

export function useAlertFeedQuery(options?: { hours?: number; severity?: string; types?: string }) {
  const client = useService();
  const { hours = 48, severity, types } = options ?? {};

  return useInfiniteQuery<AlertFeedPage, Error>(
    ['alert-feed', hours, severity, types],
    async ({ pageParam }: { pageParam?: string }) => {
      const params = new URLSearchParams();
      params.set('hours', String(hours));
      if (severity) params.set('severity', severity);
      if (types) params.set('types', types);
      if (pageParam) params.set('cursor', pageParam);
      const response = await client.fetch(`/api/alerts?${params}`);
      if (!response.ok) throw new Error(`Failed to fetch alert feed: ${response.statusText}`);
      return response.json();
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      staleTime: 30_000,
      keepPreviousData: true
    }
  );
}
